import { decodeUrnValue, isDbRegistryDatabaseUrn } from '@atbash-labs/ontology-core'
import type { ComponentScope } from './cite-tokens'

/** Child segments appended to an equipment URN (observable, setpoint, property, part). */
const EQUIPMENT_CHILD_SEGMENTS = ['/part:', '/obs:', '/sp:', '/prop:'] as const

/** Nested segments that disqualify a `/site:` URN from being a site landing link. */
const SITE_NESTED_SEGMENTS = [
  '/line:',
  '/process:',
  '/area:',
  '/compute:',
  '/tag:',
  '/tp:',
  '/db:',
  '/table:',
  '/audit:',
  '/udt-def:',
  '/udt-inst:',
  '/ctrl:',
  '/robotctrl:',
  '/arm:',
  '/job:',
  '/iosig:',
  '/cond:',
  '/prog:',
  '/routine:',
  '/aoi:',
  '/plctag:',
  '/dcsctrl:',
  '/cee:',
  '/cm:',
  '/fb:',
  '/iomod:',
  '/ioch:',
  '/asset:',
  '/netseg:',
  '/netdev:',
  '/cns:',
  '/ctag:',
  '/calarm:',
  '/csp:',
  '/blocklib:',
  '/btype:',
  '/pattern:',
  '/flow:',
  '/project:',
  '/script:',
  '/view:',
  '/component:',
  '/nq:',
  '/gw-event:',
  '/handler:',
  '/member:',
  '/param:',
  '/pf:',
  '/ls:',
  '/fld:',
  '/spoint:',
  ...EQUIPMENT_CHILD_SEGMENTS
] as const

/**
 * Ontology id segments that historically lacked a dedicated plant page.
 * Retained for call sites that still gate equipment name-resolution.
 */
const NO_PLANT_VIEW_SEGMENTS = [
  '/ctrl:',
  '/prog:',
  '/routine:',
  '/aoi:',
  '/plctag:',
  '/pattern:',
  '/flow:',
  '/db:',
  '/table:',
  '/audit:',
  '/udt-def:',
  '/udt-inst:',
  '/member:',
  '/param:',
  '/project:',
  '/script:',
  '/view:',
  '/component:',
  // `/nq:` (NamedQuery) intentionally omitted — it now has a dedicated
  // Queries-tab plant surface (see `plant-surfaces.ts`).
  '/gw-event:',
  '/handler:'
] as const

/** True when this id points at a node type with no dedicated plant surface. */
export function lacksPlantViewerPage(id: string): boolean {
  return NO_PLANT_VIEW_SEGMENTS.some((segment) => id.includes(segment))
}

/** Strip `/obs:`, `/sp:`, `/prop:`, or `/part:` suffixes to the owning equipment id. */
export function parentEquipmentIdFromChildId(id: string): string | null {
  for (const segment of EQUIPMENT_CHILD_SEGMENTS) {
    const idx = id.lastIndexOf(segment)
    if (idx > 0) return id.slice(0, idx)
  }
  return null
}

/**
 * True for a `:Line` id (`…/line:…` or `urn:line:…`). Rejects nested
 * children that reuse a line prefix in their URN (e.g. MES
 * `…/line:cell/recipe:…`).
 */
export function isLineOntologyId(id: string): boolean {
  if (!id.startsWith('urn:line:') && !id.includes('/line:')) return false
  const lineIdx = id.lastIndexOf('/line:')
  const after = lineIdx >= 0 ? id.slice(lineIdx + '/line:'.length) : id.slice('urn:line:'.length)
  return after.length > 0 && !after.includes('/')
}

/** True for a `:Part` id (`…/part:…`) that is not a deeper equipment child. */
export function isPartOntologyId(id: string): boolean {
  const idx = id.lastIndexOf('/part:')
  if (idx < 0) return false
  return !id.slice(idx + '/part:'.length).includes('/')
}

export function isProcessOntologyId(id: string): boolean {
  return id.includes('/process:')
}

/** True for a `:NamedQuery` id (`…/nq:…`) — has a dedicated Queries-tab surface. */
export function isNamedQueryOntologyId(id: string): boolean {
  return id.includes('/nq:')
}

export function isSiteOntologyId(id: string): boolean {
  if (!id.startsWith('urn:site:') && !id.includes('/site:')) return false
  return !SITE_NESTED_SEGMENTS.some((segment) => id.includes(segment))
}

export function isComputeOntologyId(id: string): boolean {
  return id.startsWith('urn:compute:') || id.includes('/compute:')
}

export function isRobotOntologyId(id: string): boolean {
  return (
    id.includes('/robotctrl:') ||
    id.includes('/arm:') ||
    id.includes('/job:') ||
    id.includes('/iosig:') ||
    id.includes('/cond:') ||
    id.startsWith('urn:robotctrl:')
  )
}

export function isDcsOntologyId(id: string): boolean {
  return (
    id.includes('/dcsctrl:') ||
    id.includes('/cee:') ||
    id.includes('/cm:') ||
    id.includes('/fb:') ||
    id.includes('/iomod:') ||
    id.includes('/ioch:') ||
    id.includes('/cns:') ||
    id.includes('/ctag:') ||
    id.includes('/calarm:') ||
    id.includes('/csp:') ||
    id.includes('/blocklib:') ||
    id.includes('/btype:') ||
    id.startsWith('urn:dcsctrl:') ||
    id.startsWith('urn:blocklib:')
  )
}

/**
 * True for an Experion EMDB enterprise/PCN id — `Asset` (`…/asset:…`),
 * `NetworkSegment` (`…/netseg:…`), or `NetworkDevice` (`…/netdev:…`) — which
 * share the Network tab. All three are site-scoped URNs.
 */
export function isNetworkOntologyId(id: string): boolean {
  return (
    id.includes('/asset:') ||
    id.includes('/netseg:') ||
    id.includes('/netdev:') ||
    id.startsWith('urn:asset:') ||
    id.startsWith('urn:netseg:') ||
    id.startsWith('urn:netdev:')
  )
}

/** True for a `:ProtectiveFunction` id (`…/pf:…`). Excludes deeper nested children. */
export function isProtectiveFunctionOntologyId(id: string): boolean {
  if (!id.startsWith('urn:pf:') && !id.includes('/pf:')) return false
  const pfIdx = id.includes('/pf:') ? id.indexOf('/pf:') + '/pf:'.length : 'urn:pf:'.length
  return !id.slice(pfIdx).includes('/')
}

/** True for an `:SrsSection` id (`…/srs-section:…`). */
export function isSrsSectionOntologyId(id: string): boolean {
  const idx = id.lastIndexOf('/srs-section:')
  if (idx < 0) return false
  return !id.slice(idx + '/srs-section:'.length).includes('/')
}

/** True for a `:LogicSolver` id (`…/ls:…`). Excludes deeper nested children. */
export function isLogicSolverOntologyId(id: string): boolean {
  if (!id.startsWith('urn:ls:') && !id.includes('/ls:')) return false
  const lsIdx = id.includes('/ls:') ? id.indexOf('/ls:') + '/ls:'.length : 'urn:ls:'.length
  return !id.slice(lsIdx).includes('/')
}

/** True for a `:FunctionLogicDiagram` id (`…/fld:…`). Excludes deeper nested children. */
export function isFunctionLogicDiagramOntologyId(id: string): boolean {
  const idx = id.lastIndexOf('/fld:')
  if (idx < 0) return false
  return !id.slice(idx + '/fld:'.length).includes('/')
}

/** True for a `:SafetyPoint` id (`…/spoint:…`). Excludes deeper nested children. */
export function isSafetyPointOntologyId(id: string): boolean {
  const idx = id.lastIndexOf('/spoint:')
  if (idx < 0) return false
  return !id.slice(idx + '/spoint:'.length).includes('/')
}

/** True for an `:Area` id (`…/area:…`). Excludes deeper nested children. */
export function isAreaOntologyId(id: string): boolean {
  if (!id.startsWith('urn:area:') && !id.includes('/area:')) return false
  const areaIdx = id.includes('/area:')
    ? id.indexOf('/area:') + '/area:'.length
    : 'urn:area:'.length
  return !id.slice(areaIdx).includes('/')
}

const TAG_PROVIDER_SEGMENT = '/tp:'
const TAG_SEGMENT = '/tag:'
const TAG_CHILD_SEGMENTS = ['/alarm:'] as const

/** True for a `:TagProvider` id (`…/tp:…`) that is not a tag or database child. */
export function isTagProviderOntologyId(id: string): boolean {
  const tpIdx = id.indexOf(TAG_PROVIDER_SEGMENT)
  if (tpIdx < 0) return false
  return !id.includes('/tag:', tpIdx) && !id.includes('/db:', tpIdx)
}

/** Split `…/compute:…/tp:…` into the owning compute host id and full provider id. */
export function splitTagProviderId(id: string): { computeId: string; providerId: string } | null {
  if (!isTagProviderOntologyId(id)) return null
  const tpIdx = id.indexOf(TAG_PROVIDER_SEGMENT)
  const computeId = id.slice(0, tpIdx)
  if (computeId.length === 0) return null
  return { computeId, providerId: id }
}

/** Split a compute-scoped Tag id into the host, provider, and provider-relative tag path. */
export function splitTagId(
  id: string
): { computeId: string; providerId: string; tagName: string } | null {
  const tpIdx = id.indexOf(TAG_PROVIDER_SEGMENT)
  if (tpIdx < 0) return null
  const tagIdx = id.indexOf(TAG_SEGMENT, tpIdx + TAG_PROVIDER_SEGMENT.length)
  if (tagIdx < 0) return null

  const tagValueIdx = tagIdx + TAG_SEGMENT.length
  if (TAG_CHILD_SEGMENTS.some((segment) => id.includes(segment, tagValueIdx))) return null

  const computeId = id.slice(0, tpIdx)
  const providerId = id.slice(0, tagIdx)
  const tagName = decodeUrnValue(id.slice(tagValueIdx))
  if (!computeId || !tagName) return null
  return { computeId, providerId, tagName }
}

/** Compute host landing — excludes tag providers and compute-scoped tag/db ids. */
export function isComputeHostOntologyId(id: string): boolean {
  if (isDbRegistryDatabaseUrn(id)) return true
  if (!isComputeOntologyId(id)) return false
  return !id.includes(TAG_PROVIDER_SEGMENT) && !id.includes('/tag:') && !id.includes('/db:')
}

/**
 * True when a stable ontology id maps to a dedicated (non-entity) plant
 * surface by URN shape. Prefer `plantHref` for navigation — every id can
 * open via the entity resolver.
 */
export function hasPlantViewerPage(id: string): boolean {
  // A NamedQuery (`…/nq:…`) has a dedicated Queries-tab surface, but its URN
  // nests under `/project:` (which `lacksPlantViewerPage` otherwise vetoes), so
  // recognize it before the generic veto.
  if (isNamedQueryOntologyId(id)) return true
  if (lacksPlantViewerPage(id)) return false
  return (
    isProtectiveFunctionOntologyId(id) ||
    isSrsSectionOntologyId(id) ||
    isLogicSolverOntologyId(id) ||
    isFunctionLogicDiagramOntologyId(id) ||
    isSafetyPointOntologyId(id) ||
    parentEquipmentIdFromChildId(id) !== null ||
    isLineOntologyId(id) ||
    isProcessOntologyId(id) ||
    isAreaOntologyId(id) ||
    isSiteOntologyId(id) ||
    isRobotOntologyId(id) ||
    isDcsOntologyId(id) ||
    isNetworkOntologyId(id) ||
    splitTagId(id) !== null ||
    isTagProviderOntologyId(id) ||
    isComputeHostOntologyId(id) ||
    id.startsWith('urn:equipment:')
  )
}

/** True when the id should deep-link to the graph highlight view by shape. */
export function hasGraphViewerPage(id: string): boolean {
  if (lacksPlantViewerPage(id)) return false
  return (id.includes(TAG_SEGMENT) && splitTagId(id) === null) || id.startsWith('urn:tag:')
}

/** True when `plantHref(id)` can produce a navigation target (always for non-empty ids). */
export function isRoutableOntologyId(id: string): boolean {
  return isLinkableOntologyId(id)
}

/** True when the id can deep-link via the entity resolver (any non-empty id). */
export function isLinkableOntologyId(id: string): boolean {
  return id.trim().length > 0
}

/** Infer cite scope from stable ontology id shape when the model picks the wrong scope. */
export function inferScopeFromOntologyId(id: string): ComponentScope | null {
  if (isPartOntologyId(id)) return null
  if (parentEquipmentIdFromChildId(id) !== null) return 'equipment'
  if (isLineOntologyId(id)) return 'process_line'
  if (id.startsWith('urn:equipment:')) return 'equipment'
  if (id.includes('/tag:') || id.startsWith('urn:tag:')) return 'tag'
  return null
}
